import { Text, Select, Field, PasswordInput, Checkbox, tokens } from '@hotjar/hotsauce';
import { useForm, Controller } from 'react-hook-form';
import styled from '@emotion/styled';

import { useTranslationWithPrefix } from '@hotjar/translations';

const TRANSLATION_DOMAIN = 'account:transfer-organization';

interface FormInputs {
  newOwner: string;
  password: string;
  confirmTransfer: string;
}

interface Props {
  onSubmit(data: FormInputs): void;
  formId: string;
  users: Array<{
    email: string | null;
    id: number;
    name: string;
  }>;
}

const OWNER_ID = 'newOwner';
const PASSWORD_ID = 'password';
const CONFIRM_ID = 'confirmTransfer';

const S = {
  Row: styled.div`
    margin-bottom: ${tokens.spacer.x3};
  `,
  CheckboxRow: styled.div`
    margin-bottom: ${tokens.spacer.x3};
    label {
      text-transform: unset;
    }
  `,
  Field: styled(Field)`
    width: 100%;
  `,
};

export function TransferOrganizationForm({ onSubmit, formId, users }: Props): React.ReactElement {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({});
  return (
    <div>
      <Text as="p">{t('info')}</Text>

      <Text as="p">{t('restriction.info')}</Text>
      <ol>
        <Text as="li">{t('restriction.rule-1')}</Text>
        <Text as="li">{t('restriction.rule-2')}</Text>
        <Text as="li">{t('restriction.rule-3')}</Text>
      </ol>

      <form
        onSubmit={handleSubmit((data) => {
          onSubmit(data);
        })}
        id={formId}
      >
        <S.Row>
          <S.Field
            labelText={t('owner-label')}
            invalidText={(!!errors.newOwner && errors.newOwner.message) || t('required')}
            inputId={OWNER_ID}
            size="m"
            isInvalid={!!errors.newOwner}
            isRequired
          >
            <Select
              id={OWNER_ID}
              name={OWNER_ID}
              ref={register({
                required: true,
              })}
              isInvalid={!!errors.newOwner}
            >
              <option value="">{t('owner-placeholder')}</option>
              {users.map(({ name, email, id }) => (
                <option value={id} key={id}>
                  {name} - {email}
                </option>
              ))}
            </Select>
          </S.Field>
        </S.Row>
        <S.Row>
          <S.Field
            labelText={t('password-label')}
            invalidText={(!!errors.password && errors.password.message) || t('required')}
            inputId={PASSWORD_ID}
            size="m"
            isInvalid={!!errors.password}
            isRequired
          >
            <PasswordInput
              id={PASSWORD_ID}
              placeholder={t('password-placeholder')}
              name={PASSWORD_ID}
              ref={register({
                required: true,
              })}
              isInvalid={!!errors.password}
            />
          </S.Field>
        </S.Row>
        <S.CheckboxRow>
          <Controller
            control={control}
            name={CONFIRM_ID}
            defaultValue={false}
            render={({ onChange, value }) => (
              <Checkbox
                id={CONFIRM_ID}
                labelContent={t('understand')}
                name={CONFIRM_ID}
                isChecked={value}
                onChange={() => onChange(!value)}
                isInvalid={!!errors.confirmTransfer}
                isRequired
              />
            )}
          />
        </S.CheckboxRow>
      </form>
    </div>
  );
}
